import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

interface VideoData {
  url: string;
}

interface AppContextValue {
  responsiveMenuActive: boolean;
  toggleResponsiveMenu: () => void;
  video: VideoData | null;
  openVideoModal: (video: VideoData | null) => void;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [responsiveMenuActive, setResponsiveMenuActive] = useState(false);
  const [video, setVideo] = useState<VideoData | null>(null);

  const toggleResponsiveMenu = useCallback(() => {
    setResponsiveMenuActive((prev) => !prev);
  }, []);

  const openVideoModal = useCallback((videoData: VideoData | null) => {
    setVideo(videoData);
  }, []);

  // Memoize the context value to prevent infinite re-renders
  const value: AppContextValue = useMemo(
    () => ({
      responsiveMenuActive,
      toggleResponsiveMenu,
      video,
      openVideoModal,
    }),
    [responsiveMenuActive, toggleResponsiveMenu, video, openVideoModal]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

